import { useCallback, useEffect, useRef, useState } from "react";
import getApiBase from "../utils/apiBase";

/** Marker the backend popup-close page stamps on its postMessage payload.
 * Generic across any OAuth-based flow (connectors, Slack, ...) — the
 * backend's `render_popup_close_html` always uses this same source tag. */
export const OAUTH_MESSAGE_SOURCE = "magicbi-connector-oauth";

export interface UseOAuthPopupArgs {
  /** Fetch the provider's authorize URL for a fresh popup-mode attempt. */
  fetchAuthorizeUrl: () => Promise<string>;
  /** Full-page fallback when the popup is blocked by the browser. */
  beginFullPageRedirect: () => Promise<void>;
  /** Called with the postMessage payload (minus `source`) on success. */
  onConnected: (data: Record<string, unknown>) => void;
  /** Called with a user-facing message when consent fails/cancels. */
  onError: (message: string) => void;
  /** Called whenever the popup closes for ANY reason — success, failure, or
   * the user closing it manually — as a safety-net re-check. The
   * `postMessage` success signal can be missed (e.g. a stale popup/listener
   * from a rapid double-click, a race with the tunnel/proxy in dev), so
   * callers should use this to force a fresh status refetch rather than
   * relying solely on `onConnected` firing. No-ops if `onConnected`/`onError`
   * already ran for this attempt (popupRef is already null by then). */
  onPopupClosed?: () => void;
}

export interface UseOAuthPopupResult {
  authorizing: boolean;
  startOAuth: () => Promise<void>;
}

/** Origins we accept the popup's postMessage from. The popup close-page is
 *  served by the backend (API base origin); allow the app origin too as a
 *  defensive fallback for same-origin/proxy setups. */
const allowedOrigins = (): Set<string> => {
  const origins = new Set<string>();
  try {
    origins.add(new URL(getApiBase()).origin);
  } catch {
    /* ignore malformed base */
  }
  if (typeof window !== "undefined") origins.add(window.location.origin);
  return origins;
};

/**
 * Generic engine behind any "connect via OAuth popup" button: opens a popup,
 * listens for the backend's postMessage result, and falls back to a
 * full-page redirect if the popup is blocked. Extracted from what was
 * originally Salesforce-only logic (`useConnectorOAuthPopup`) so Slack (and
 * any future OAuth-based channel/connector) can reuse the exact same
 * mechanics instead of re-implementing popup/postMessage handling.
 *
 * The popup is opened **synchronously** inside `startOAuth` (before any
 * await) so browser popup blockers — which only allow `window.open` during a
 * user gesture — don't block it.
 */
export const useOAuthPopup = ({
  fetchAuthorizeUrl,
  beginFullPageRedirect,
  onConnected,
  onError,
  onPopupClosed,
}: UseOAuthPopupArgs): UseOAuthPopupResult => {
  const [authorizing, setAuthorizing] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const messageHandlerRef = useRef<((e: MessageEvent) => void) | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Latest callbacks, so the message handler never calls a stale closure.
  const fetchAuthorizeUrlRef = useRef(fetchAuthorizeUrl);
  const beginFullPageRedirectRef = useRef(beginFullPageRedirect);
  const onConnectedRef = useRef(onConnected);
  const onErrorRef = useRef(onError);
  const onPopupClosedRef = useRef(onPopupClosed);
  fetchAuthorizeUrlRef.current = fetchAuthorizeUrl;
  beginFullPageRedirectRef.current = beginFullPageRedirect;
  onConnectedRef.current = onConnected;
  onErrorRef.current = onError;
  onPopupClosedRef.current = onPopupClosed;

  const cleanup = useCallback(() => {
    if (messageHandlerRef.current) {
      window.removeEventListener("message", messageHandlerRef.current);
      messageHandlerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearInterval(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    popupRef.current = null;
    setAuthorizing(false);
  }, []);

  // Tear everything down if the component unmounts mid-flow.
  useEffect(() => cleanup, [cleanup]);

  const startOAuth = useCallback(async () => {
    // Open the popup NOW (synchronous, inside the click) to dodge blockers.
    const popup = window.open(
      "",
      "oauth_popup",
      "width=600,height=720,menubar=no,toolbar=no",
    );
    if (!popup) {
      // Blocked → fall back to full-page redirect (leaves the app, but works).
      await beginFullPageRedirectRef.current();
      return;
    }

    popupRef.current = popup;
    setAuthorizing(true);

    const origins = allowedOrigins();
    const handleMessage = (event: MessageEvent) => {
      if (!origins.has(event.origin)) return;
      const data = event.data;
      if (!data || data.source !== OAUTH_MESSAGE_SOURCE) return;
      cleanup();
      try {
        popup.close();
      } catch {
        /* already closed */
      }
      // "oauth" (not "status") is the wire contract's status key — matches
      // the connectors backend's existing `{oauth: "connected"|"error", ...}`
      // payload shape (see oauth_authcode_service.py), which we must not
      // change since Salesforce already ships against it.
      const { oauth, error, ...rest } = data;
      if (oauth === "connected") {
        onConnectedRef.current(rest);
      } else {
        onErrorRef.current(
          error ? `Connection failed (${error}).` : "Connection failed. Please try again.",
        );
      }
    };
    messageHandlerRef.current = handleMessage;
    window.addEventListener("message", handleMessage);

    // If the popup closes without the message handler having already run
    // (popupRef is still set), reset state AND trigger the safety-net
    // re-check — covers both "user closed it manually" and "the success
    // postMessage was sent but missed" (see onPopupClosed doc above).
    closeTimerRef.current = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        cleanup();
        onPopupClosedRef.current?.();
      }
    }, 700);

    try {
      const url = await fetchAuthorizeUrlRef.current();
      if (popupRef.current) popupRef.current.location.href = url;
    } catch (error) {
      cleanup();
      try {
        popup.close();
      } catch {
        /* ignore */
      }
      onErrorRef.current(
        error instanceof Error ? error.message : "Couldn't start sign-in. Please try again.",
      );
    }
  }, [cleanup]);

  return { authorizing, startOAuth };
};

export default useOAuthPopup;
